import { useState, useEffect, useRef, useCallback } from "preact/hooks";
import { PopupContext } from "../context";

export const PopupProvider = ({ children }) => {
  const [popups, setPopups] = useState([]);
  const isClosingRef = useRef(false);

  const showPopup = useCallback((content) => {
    setPopups((prev) => [...prev, content]);

    if (typeof document !== "undefined") {
      document.body.classList.add("has-popup");

      // Hide background page from screen readers
      const appWrapper = document.getElementById("app-content");
      if (appWrapper) {
        appWrapper.setAttribute("aria-hidden", "true");
      }
    }
  }, []);

  const cleanupDOM = () => {
    if (typeof document !== "undefined") {
      document.body.classList.remove("has-popup");

      // Restore background page for screen readers
      const appWrapper = document.getElementById("app-content");
      if (appWrapper) {
        appWrapper.removeAttribute("aria-hidden");
      }
    }
  };

  const hidePopup = useCallback(() => {
    // Prevent multiple simultaneous close events (like pressing Escape key) from closing all modals at once
    if (isClosingRef.current) {
      return;
    }
    isClosingRef.current = true;
    setTimeout(() => {
      isClosingRef.current = false;
    }, 100);

    setPopups((prev) => {
      const nextPopups = prev.slice(0, -1);

      if (nextPopups.length === 0) {
        cleanupDOM();
      }
      return nextPopups;
    });
  }, []);

  // Automatically close popups and clean up DOM attributes on route navigation
  useEffect(() => {
    const handleRouteChange = () => {
      setPopups([]);
      cleanupDOM();
    };

    if (typeof window !== "undefined") {
      window.addEventListener("route-change", handleRouteChange);
    }

    return () => {
      if (typeof window !== "undefined") {
        window.removeEventListener("route-change", handleRouteChange);
      }
    };
  }, []);

  return (
    <PopupContext.Provider
      value={{
        open: popups.length > 0,
        content: popups.length > 0 ? popups[popups.length - 1] : null,
        popups,
        showPopup,
        hidePopup,
      }}
    >
      {children}
    </PopupContext.Provider>
  );
};
